import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { adminLogin } from "../services/adminAuthService";
import "../adminStyle/adminLoginPage.css";
import illustration from "../images/illustration1.png"
import logo from "../images/logo.png"
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

export default function AdminLoginPage() {
  const [showPassword, setShowPassword] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const navigate = useNavigate();

  const preventPasteShortcut = (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "v") {
      e.preventDefault();
    }
  };

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setLoading(true);

    let captchaToken = null;
    try {
      if (!executeRecaptcha) {
        setError("reCAPTCHA not ready. Please refresh and try again.");
        setLoading(false);
        return;
      }
      captchaToken = await executeRecaptcha("admin_login");
    } catch (err) {
      setError("reCAPTCHA failed. Please try again.");
      setLoading(false);
      return;
    }

    const result = await adminLogin(username, password, captchaToken);
    setLoading(false);
    if (result.success) {
      navigate("/dashboard", { replace: true });
    } else {
      setError(result.error);
    }
  }

  return (
    <div className="admin-login-page">
      <div className="admin-layout">
        {/* LEFT SIDE */}
        <div className="corner-banner">
          <div className="banner-content">
            <img src={logo} alt="Jerry's Bins Logo" />
            <p>Jerry's Bins</p>
          </div>
          <svg
            className="corner-svg"
            width="550"
            height="320"
            viewBox="0 0 550 320"
            fill="none"
          >
            <path
              d="M 0 0 L 480 0 L 415 45 C 375 75 330 84 250 84 C 150 84 110 110 70 165 L 0 300 Z"
              fill="#1B2A4A"
            />
          </svg>
        </div>
        <div className="admin-left">
          <div className="admin-left-shape">
            <div className="admin-illustration">
              <img src={illustration} alt="Waste Management" />
            </div>
          </div>
          <svg viewBox="0 0 600 600" preserveAspectRatio="none">
            <polygon
              points="523,0 560,0 576,312 "
              fill="#d0d5e0"
              stroke="#969ab6"
              strokeWidth="1"
            />
            <polygon
              points="576,312 487,460 475,461 "
              fill="#d0d5e0"
              stroke="#969ab6"
              strokeWidth="1"
            />
            <polygon
              points="475,461 487,460 490,550 380,599 281,599 462,546"
              fill="#d0d5e0"
              stroke="#969ab6"
              strokeWidth="1"
            />
            <polygon
              points="490,550 430,599 380,599"
              fill="#f5f6fa"
              stroke="#969ab6"
              strokeWidth="1"
            />
          </svg>
        </div>

        {/* RIGHT SIDE */}
        <div className="admin-right">
          <div className="admin-right-shape">
            <svg
              viewBox="-60 0 600 700"
              preserveAspectRatio="xMidYMid meet"
              style={{ shapeRendering: "crispEdges" }}
            >
              {/* Top-left dark sliver */}
              <polygon
                points="0,130 120,50 310,130 130,160"
                fill="#f5f6fa"
                stroke="#969ab6"
                strokeWidth="2"
              />
              {/* Top center light */}
              <polygon
                points="120,50 340,50 310,130 "
                fill="#d0d5e0"
                stroke="#969ab6"
                strokeWidth="2"
              />
              {/* Top-right dark */}
              <polygon
                points="340,50 440,80 500,220 380,180 310,130"
                fill="#f5f6fa"
                stroke="#969ab6"
                strokeWidth="2"
              />
              {/* Left mid dark */}
              <polygon
                points="0,130 130,160 60,290 20,400"
                fill="#d0d5e0"
                stroke="#969ab6"
                strokeWidth="2"
              />
              {/* Left mid light */}
              <polygon
                points="60,290 130,160 310,130 380,180 280,310  "
                fill="#f5f6fa"
                stroke="#969ab6"
                strokeWidth="2"
              />
              {/* Lower-left dark */}
              <polygon
                points="60,290 280,310 260,480 160,500 80,460 20,400"
                fill="#f5f6fa"
                stroke="#969ab6"
                strokeWidth="2"
              />
              {/* Lower center-right medium */}
              <polygon
                points="280,310 400,300 420,460 260,480"
                fill="#cdd0dc"
                stroke="#969ab6"
                strokeWidth="2"
              />
              {/* Lower right dark */}
              <polygon
                points="380,180 500,220 500,500 420,460"
                fill="#f5f6fa"
                stroke="#969ab6"
                strokeWidth="2"
              />

              {/* Bottom-left dark sliver */}
              <polygon
                points="20,400 80,460 20,680"
                fill="#f5f6fa"
                stroke="#969ab6"
                strokeWidth="2"
              />
              {/* Bottom-left light */}
              <polygon
                points="20,680 80,460 160,500 140,620"
                fill="#e0e3ee"
                stroke="#969ab6"
                strokeWidth="2"
              />

              {/* Bottom center */}
              <polygon
                points="160,500 260,480 240,620 140,620"
                fill="#f5f6fa"
                stroke="#969ab6"
                strokeWidth="2"
              />
              {/* Bottom center-right */}
              <polygon
                points="260,480 420,460 400,600 240,620"
                fill="#f5f6fa"
                stroke="#969ab6"
                strokeWidth="2"
              />
              {/* Bottom-right dark */}
              <polygon
                points="420,460 500,500 500,640 470,680 400,600"
                fill="#d0d5e0"
                stroke="#969ab6"
                strokeWidth="2"
              />
            </svg>
          </div>

          <div className="admin-login-card">
            <h2 className="admin-login-title">Admin Portal</h2>

            {error && <div className="admin-login-error">{error}</div>}

            <form className="admin-login-form" onSubmit={handleSubmit}>
              <div className="admin-form-group">
                <label>Username</label>
                <input
                  type="text"
                  placeholder="Enter your username"
                  autoComplete="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  onPaste={(e) => e.preventDefault()}
                  onDrop={(e) => e.preventDefault()}
                  onKeyDown={preventPasteShortcut}
                  required
                  className="admin-form-input"
                />
              </div>

              <div className="admin-form-group">
                <label>Password</label>
                <div className="admin-password-wrap">
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onPaste={(e) => e.preventDefault()}
                    onDrop={(e) => e.preventDefault()}
                    onKeyDown={preventPasteShortcut}
                    required
                    className="admin-form-input"
                  />
                  <button
                    type="button"
                    className="admin-password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    <i
                      className={showPassword ? "ti ti-eye-off" : "ti ti-eye"}
                    ></i>
                  </button>
                </div>
              </div>

              <button
                type="submit"
                className="admin-btn-primary"
                disabled={loading}
              >
                {loading ? "Signing In..." : "Login to Admin Portal"}
              </button>
            </form>

            <div className="admin-login-links">
              <button
                type="button"
                className="admin-forgot-link"
                onClick={() => navigate("/reset-password")}
              >
                Forgot Password?
              </button>

              <a href="/">Back to Website →</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
